import { useCallback, useEffect, useRef } from "react"
import { Modal } from "components/ui/modals/Modal/Modal"
import { Checkbox } from "components/ui/checkbox/Checkbox"
import type { ComponentProps } from "react"
import style from "./ConfirmModal.module.scss"

type Props = {
	isOpen: boolean
	onClose: () => void
	onConfirm: () => void
	title: string
	description: string
	confirmLabel?: string
	cancelLabel?: string
	isPending?: boolean
	// When provided, overrides the default WebViewer shadow-root portal logic.
	// Pass `false` to render at the page level (over a page-level modal) instead of inside the viewer.
	getContainer?: ComponentProps<typeof Modal>["getContainer"]
	/**
	 * Optional opt-out checkbox rendered on the LEFT of the action row (REQ-403's category-change
	 * confirmation). The caller owns the flag — the modal only reports the tick, so whether it is
	 * remembered for the session, the user or forever is the caller's decision.
	 */
	dontShowAgain?: {
		checked: boolean
		onChange: (checked: boolean) => void
		/** Overrides the default "Don't show this again" wording. */
		label?: string
	}
}

export const ConfirmModal = ({
	isOpen,
	onClose,
	onConfirm,
	title,
	description,
	confirmLabel = "Confirm",
	cancelLabel = "Cancel",
	isPending = false,
	getContainer,
	dontShowAgain,
}: Props) => {
	const anchorRef = useRef<HTMLDivElement | null>(null)
	const portalRef = useRef<HTMLDivElement | null>(null)

	useEffect(() => {
		return () => {
			portalRef.current?.remove()
			portalRef.current = null
		}
	}, [])

	const defaultGetContainer = useCallback((): HTMLElement => {
		if (portalRef.current?.isConnected) return portalRef.current
		const root = anchorRef.current?.getRootNode()
		const host: ShadowRoot | HTMLElement =
			root instanceof ShadowRoot
				? root
				: ((document.querySelector("apryse-webviewer") as HTMLElement | null)?.shadowRoot ?? document.body)
		const container = document.createElement("div")
		container.setAttribute("data-portal", "confirm-modal")
		host.appendChild(container)
		portalRef.current = container
		return container
	}, [])

	const resolvedGetContainer = getContainer !== undefined ? getContainer : defaultGetContainer

	return (
		<>
			<div ref={anchorRef} style={{ display: "none" }} aria-hidden />
			<Modal isModalOpen={isOpen} setModalClose={onClose} width={456} getContainer={resolvedGetContainer}>
				<div className={style.body} data-testid="confirm-modal">
					<h3 className={style.title}>{title}</h3>
					<p className={style.description}>{description}</p>
					<div className={style.actions}>
						{dontShowAgain ? (
							<label className={style.dontShowAgain}>
								<Checkbox
									checked={dontShowAgain.checked}
									onChange={(event) => dontShowAgain.onChange(event.target.checked)}
									disabled={isPending}
								/>
								<span>{dontShowAgain.label ?? "Don't show this again"}</span>
							</label>
						) : null}
						<button type="button" className={style.cancelButton} onClick={onClose} disabled={isPending}>
							{cancelLabel}
						</button>
						<button type="button" className={style.confirmButton} onClick={onConfirm} disabled={isPending}>
							{confirmLabel}
						</button>
					</div>
				</div>
			</Modal>
		</>
	)
}
